export const MAX_TASK_LEVEL = 2;

export const normalizeParentId = id => (id == null || id === '' ? null : id);

export const buildChildrenMap = tasks => {
    const map = new Map();
    (tasks || []).forEach(task => {
        const parentId = normalizeParentId(task?.parent_task);
        if (parentId == null) return;
        const siblings = map.get(parentId);
        if (siblings) siblings.push(task);
        else map.set(parentId, [task]);
    });
    return map;
};

export const getChildren = (parentId, tasks, childrenMap = buildChildrenMap(tasks)) => {
    const normalizedParentId = normalizeParentId(parentId);
    return childrenMap.get(normalizedParentId) || [];
};

export const getRootTasks = tasks => {
    return sortTasksByOrder(
        (tasks || []).filter(task => normalizeParentId(task?.parent_task) == null),
        null,
    );
};

export const buildTaskMap = tasks => new Map((tasks || []).map(task => [task.id, task]));

export const getTaskLevel = (taskId, tasks, taskMap = buildTaskMap(tasks)) => {
    let level = 0;
    let current = taskMap.get(taskId);

    while (current && normalizeParentId(current.parent_task) != null) {
        level += 1;
        current = taskMap.get(normalizeParentId(current.parent_task));
        if (level > MAX_TASK_LEVEL + 1) break;
    }

    return level;
};

export const isDescendant = (potentialDescendantId, ancestorId, tasks, childrenMap = buildChildrenMap(tasks)) => {
    const stack = [...getChildren(ancestorId, tasks, childrenMap)];

    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) continue;
        if (current.id === potentialDescendantId) return true;
        stack.push(...getChildren(current.id, tasks, childrenMap));
    }

    return false;
};

export const getMaxDescendantLevel = (taskId, tasks, childrenMap = buildChildrenMap(tasks)) => {
    let maxDepth = 0;

    const walk = (id, depth) => {
        maxDepth = Math.max(maxDepth, depth);
        getChildren(id, tasks, childrenMap).forEach(child => walk(child.id, depth + 1));
    };

    getChildren(taskId, tasks, childrenMap).forEach(child => walk(child.id, 1));
    return maxDepth;
};

export const wouldExceedMaxDepth = (draggedTaskId, newParentId, tasks, childrenMap = buildChildrenMap(tasks), taskMap = buildTaskMap(tasks)) => {
    const newParentLevel = newParentId != null ? getTaskLevel(newParentId, tasks, taskMap) + 1 : 0;
    const subtreeDepth = getMaxDescendantLevel(draggedTaskId, tasks, childrenMap);
    return newParentLevel + subtreeDepth > MAX_TASK_LEVEL;
};

/** Pre-built maps for drag intent resolution (avoids rebuilding per pointer move). */
export const buildTaskHierarchyIndex = tasks => ({
    childrenMap: buildChildrenMap(tasks),
    taskMap: buildTaskMap(tasks),
});

export const getSiblingTasks = (tasks, parentId) => {
    const normalizedParentId = normalizeParentId(parentId);
    return (tasks || []).filter(task => normalizeParentId(task.parent_task) === normalizedParentId);
};

export const sortTasksByOrder = (tasks, customOrder) => {
    if (customOrder?.length) {
        const ordered = [];
        customOrder.forEach(taskId => {
            const task = tasks.find(item => item.id === taskId);
            if (task) ordered.push(task);
        });

        tasks.forEach(task => {
            if (!customOrder.includes(task.id)) ordered.push(task);
        });

        return ordered;
    }

    return [...tasks].sort((a, b) => {
        const orderDiff = (a.order ?? 0) - (b.order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        const aCreated = a.created ? new Date(a.created).getTime() : 0;
        const bCreated = b.created ? new Date(b.created).getTime() : 0;
        if (aCreated !== bCreated) return aCreated - bCreated;
        return (a.id ?? 0) - (b.id ?? 0);
    });
};

export const buildHierarchyFlatOrder = (tasks, customOrder, expandedTasks = null) => {
    const ids = [];
    const walk = taskList => {
        taskList.forEach(task => {
            ids.push(task.id);
            const children = sortTasksByOrder(getChildren(task.id, tasks), customOrder);
            if (children.length > 0 && (expandedTasks == null || expandedTasks.has(task.id))) {
                walk(children);
            } else if (children.length > 0 && expandedTasks == null) {
                walk(children);
            }
        });
    };

    walk(sortTasksByOrder(getRootTasks(tasks), customOrder));

    (tasks || []).forEach(task => {
        if (!ids.includes(task.id)) ids.push(task.id);
    });

    return ids;
};

export const flattenVisibleTaskIds = (tasks, expandedTasks, customOrder) => {
    return buildHierarchyFlatOrder(tasks, customOrder, expandedTasks).map(String);
};

export const getTaskIdsInVisibleRange = (visibleIds, fromId, toId) => {
    if (toId == null) return [];
    if (fromId == null) return [toId];

    const fromIndex = visibleIds.findIndex(id => String(id) === String(fromId));
    const toIndex = visibleIds.findIndex(id => String(id) === String(toId));
    if (fromIndex === -1 || toIndex === -1) return [toId];

    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    return visibleIds.slice(start, end + 1);
};

export const getPromotedParentId = (draggedTask, tasks, promoteToRoot = false) => {
    if (promoteToRoot) return null;

    const currentParentId = normalizeParentId(draggedTask?.parent_task);
    if (currentParentId == null) return null;

    const parentTask = tasks.find(task => task.id === currentParentId);
    return normalizeParentId(parentTask?.parent_task);
};

export const resolveDropIntent = (draggedTask, targetTask, tasks, { pointerY, overRect, deltaX }, hierarchyIndex = null) => {
    if (!draggedTask || !targetTask || draggedTask.id === targetTask.id) {
        return { valid: false };
    }

    const childrenMap = hierarchyIndex?.childrenMap ?? buildChildrenMap(tasks);
    const taskMap = hierarchyIndex?.taskMap ?? buildTaskMap(tasks);

    if (isDescendant(targetTask.id, draggedTask.id, tasks, childrenMap)) {
        return { valid: false, reason: 'circular' };
    }

    const yRatio = overRect?.height ? (pointerY - overRect.top) / overRect.height : 0.5;

    if (deltaX <= -120) {
        const parentId = null;
        if (wouldExceedMaxDepth(draggedTask.id, parentId, tasks, childrenMap, taskMap)) {
            return { valid: false, reason: 'maxDepth' };
        }
        return { valid: true, label: 'promote', parentId, insertMode: 'before', relativeTo: targetTask.id };
    }

    if (deltaX <= -50) {
        const parentId = getPromotedParentId(draggedTask, tasks);
        if (wouldExceedMaxDepth(draggedTask.id, parentId, tasks, childrenMap, taskMap)) {
            return { valid: false, reason: 'maxDepth' };
        }
        return { valid: true, label: 'promote', parentId, insertMode: 'before', relativeTo: targetTask.id };
    }

    if (yRatio >= 2 / 3) {
        const parentId = targetTask.id;
        if (wouldExceedMaxDepth(draggedTask.id, parentId, tasks, childrenMap, taskMap)) {
            return { valid: false, reason: 'maxDepth' };
        }
        return { valid: true, label: 'nest', parentId, insertMode: 'child', relativeTo: targetTask.id };
    }

    const parentId = normalizeParentId(targetTask.parent_task);
    if (wouldExceedMaxDepth(draggedTask.id, parentId, tasks, childrenMap, taskMap)) {
        return { valid: false, reason: 'maxDepth' };
    }

    if (yRatio >= 1 / 3) {
        return { valid: true, label: 'reorder-after', parentId, insertMode: 'after', relativeTo: targetTask.id };
    }

    return { valid: true, label: 'reorder', parentId, insertMode: 'before', relativeTo: targetTask.id };
};

export const applyTaskMove = (tasks, draggedTask, targetTask, intent, customOrder) => {
    const nextParentId = normalizeParentId(intent.parentId);
    const updatedDraggedTask = { ...draggedTask, parent_task: nextParentId };
    const tasksWithUpdatedParent = tasks.map(task => (task.id === draggedTask.id ? updatedDraggedTask : task));

    let siblings;
    let insertAt;

    if (intent.insertMode === 'child') {
        siblings = sortTasksByOrder(getChildren(intent.relativeTo, tasksWithUpdatedParent), customOrder).filter(task => task.id !== draggedTask.id);
        insertAt = siblings.length;
    } else {
        siblings = sortTasksByOrder(getSiblingTasks(tasksWithUpdatedParent, nextParentId), customOrder).filter(task => task.id !== draggedTask.id);
        const targetIndex = siblings.findIndex(task => task.id === targetTask.id);
        if (intent.insertMode === 'after') {
            insertAt = targetIndex === -1 ? siblings.length : targetIndex + 1;
        } else {
            insertAt = targetIndex === -1 ? siblings.length : targetIndex;
        }
    }

    siblings.splice(insertAt, 0, updatedDraggedTask);

    const siblingIds = new Set(siblings.map(task => task.id));
    const baseOrder = customOrder?.length ? [...customOrder] : tasks.map(task => task.id);
    const withoutMoved = baseOrder.filter(id => !siblingIds.has(id));
    const parentId = normalizeParentId(siblings[0]?.parent_task);

    let nextCustomOrder;
    if (parentId != null) {
        const parentIndex = withoutMoved.indexOf(parentId);
        if (parentIndex !== -1) {
            withoutMoved.splice(parentIndex + 1, 0, ...siblings.map(task => task.id));
            nextCustomOrder = withoutMoved;
        } else {
            nextCustomOrder = buildHierarchyFlatOrder(tasksWithUpdatedParent, baseOrder);
        }
    } else {
        const targetPos = baseOrder.indexOf(targetTask.id);
        let insertIndex = withoutMoved.length;

        if (targetPos !== -1) {
            if (intent.insertMode === 'after') {
                const nextAnchor = baseOrder.slice(targetPos + 1).find(id => withoutMoved.includes(id));
                insertIndex = nextAnchor != null ? withoutMoved.indexOf(nextAnchor) : withoutMoved.length;
            } else {
                const anchor = baseOrder.slice(targetPos).find(id => withoutMoved.includes(id));
                insertIndex = anchor != null ? withoutMoved.indexOf(anchor) : withoutMoved.length;
            }
        }

        withoutMoved.splice(insertIndex, 0, ...siblings.map(task => task.id));
        nextCustomOrder = withoutMoved;
    }

    if (!nextCustomOrder) {
        nextCustomOrder = buildHierarchyFlatOrder(tasksWithUpdatedParent, baseOrder);
    }

    return {
        tasksWithUpdatedParent,
        orderedSiblings: siblings,
        nextCustomOrder: buildHierarchyFlatOrder(tasksWithUpdatedParent, nextCustomOrder),
    };
};

export const getDropIntentMetrics = (pointer, overRect, deltaX = 0) => ({
    pointerY: pointer?.y ?? (overRect ? overRect.top + overRect.height / 2 : 0),
    overRect,
    deltaX,
});

export const getDropIntentMessage = intent => {
    if (!intent) return '';

    if (!intent.valid) {
        if (intent.reason === 'circular') return 'Cannot nest inside its own subtask';
        if (intent.reason === 'maxDepth') return 'Maximum 3 levels reached';
        return 'Cannot drop here';
    }

    if (intent.label === 'nest') return 'Make subtask';
    if (intent.label === 'reorder-after') return 'Place after this task';
    if (intent.label === 'reorder') return 'Place before this task';
    if (intent.label === 'promote') {
        return intent.parentId == null ? 'Move to top level' : 'Move up one level';
    }

    return '';
};

/**
 * Build the minimal reorder API payload after a drag-and-drop move.
 * Order is scoped per sibling group (same parent), not a global flat index.
 */
export const buildTaskReorderPayload = (tasks, draggedTask, orderedSiblings, oldParentId, newParentId) => {
    if (!orderedSiblings?.length) {
        return [];
    }

    const normalizedOldParentId = normalizeParentId(oldParentId);
    const normalizedNewParentId = normalizeParentId(
        newParentId ?? orderedSiblings[0]?.parent_task,
    );
    const payload = [];
    const seen = new Set();

    const appendGroup = (siblings, parentId) => {
        const normalizedParentId = normalizeParentId(parentId);
        const siblingLevel = normalizedParentId == null ? 0 : getTaskLevel(normalizedParentId, tasks) + 1;
        siblings.forEach((task, index) => {
            if (seen.has(task.id)) return;
            seen.add(task.id);
            payload.push({
                id: task.id,
                order: index,
                parent_task: normalizedParentId,
                level: siblingLevel,
            });
        });
    };

    appendGroup(orderedSiblings, normalizedNewParentId);

    if (normalizedOldParentId !== normalizedNewParentId) {
        const remainingOldSiblings = sortTasksByOrder(
            getSiblingTasks(tasks, normalizedOldParentId).filter(task => task.id !== draggedTask.id),
            null,
        );
        appendGroup(remainingOldSiblings, normalizedOldParentId);
    }

    return payload;
};

export const getAncestorIds = (task, tasks) => {
    const ids = [];
    let current = task;

    while (current && normalizeParentId(current.parent_task) != null) {
        const parentId = normalizeParentId(current.parent_task);
        ids.push(parentId);
        current = tasks.find(item => item.id === parentId);
    }

    return ids;
};

const toValidDate = value => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toDateInputValue = date => {
    if (!date) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

/**
 * Parent/child timelines roll up from descendants:
 * - start = earliest start among the subtree
 * - end = latest end among the subtree
 * Leaf tasks keep their own dates.
 */
export const getInheritedTimeline = (task, tasks, childrenMap = buildChildrenMap(tasks), cache = new Map()) => {
    if (!task?.id) {
        return { start_date: null, end_date: null, isInherited: false };
    }

    if (cache.has(task.id)) return cache.get(task.id);

    const children = getChildren(task.id, tasks, childrenMap);
    if (children.length === 0) {
        const own = {
            start_date: task.start_date || null,
            end_date: task.end_date || null,
            isInherited: false,
        };
        cache.set(task.id, own);
        return own;
    }

    let earliestStart = null;
    let latestEnd = null;

    children.forEach(child => {
        const childTimeline = getInheritedTimeline(child, tasks, childrenMap, cache);
        const childStart = toValidDate(childTimeline.start_date);
        const childEnd = toValidDate(childTimeline.end_date);

        if (childStart && (!earliestStart || childStart < earliestStart)) {
            earliestStart = childStart;
        }
        if (childEnd && (!latestEnd || childEnd > latestEnd)) {
            latestEnd = childEnd;
        }
    });

    const inherited = {
        start_date: toDateInputValue(earliestStart),
        end_date: toDateInputValue(latestEnd),
        isInherited: true,
    };
    cache.set(task.id, inherited);
    return inherited;
};

/**
 * Parent/child deadlines roll up from descendants:
 * - deadline = latest deadline among the subtree
 * Leaf tasks keep their own deadline.
 */
export const getInheritedDeadline = (task, tasks, childrenMap = buildChildrenMap(tasks), cache = new Map()) => {
    if (!task?.id) {
        return { deadline: null, isInherited: false };
    }

    if (cache.has(task.id)) return cache.get(task.id);

    const children = getChildren(task.id, tasks, childrenMap);
    if (children.length === 0) {
        const own = {
            deadline: task.deadline || null,
            isInherited: false,
        };
        cache.set(task.id, own);
        return own;
    }

    let latestDeadline = null;

    children.forEach(child => {
        const childDeadline = getInheritedDeadline(child, tasks, childrenMap, cache);
        const childDate = toValidDate(childDeadline.deadline);
        if (childDate && (!latestDeadline || childDate > latestDeadline)) {
            latestDeadline = childDate;
        }
    });

    const inherited = {
        deadline: toDateInputValue(latestDeadline),
        isInherited: true,
    };
    cache.set(task.id, inherited);
    return inherited;
};

/**
 * Parent duration rolls up from descendants by summing child durations.
 * Prefer `task.duration` from the API when present (Phase 1 engine sum rollup).
 */
export const getInheritedDuration = (task, tasks, childrenMap = buildChildrenMap(tasks), cache = new Map()) => {
    if (!task?.id) {
        return { duration: null, isInherited: false };
    }

    if (cache.has(task.id)) return cache.get(task.id);

    const children = getChildren(task.id, tasks, childrenMap);
    if (children.length === 0) {
        const own = {
            duration: task.duration ?? null,
            isInherited: false,
        };
        cache.set(task.id, own);
        return own;
    }

    // Prefer server-rolled parent duration (sum of descendant leaves, in minutes)
    if (task.duration != null && task.duration !== '') {
        const inherited = {
            duration: Number(task.duration),
            isInherited: true,
        };
        cache.set(task.id, inherited);
        return inherited;
    }

    let total = 0;
    let hasValue = false;

    children.forEach(child => {
        const childDuration = getInheritedDuration(child, tasks, childrenMap, cache);
        if (childDuration.duration != null && childDuration.duration !== '') {
            const num = Number(childDuration.duration);
            if (!Number.isNaN(num)) {
                total += num;
                hasValue = true;
            }
        }
    });

    const inherited = {
        duration: hasValue ? total : null,
        isInherited: true,
    };
    cache.set(task.id, inherited);
    return inherited;
};
